import {ChangeDetectorRef, Component, OnInit,
  CUSTOM_ELEMENTS_SCHEMA} from '@angular/core';
import {DcpProductDeterminationService} from '@app/services/dcp-product-determination.service';
import {ConfigService, ProductDetermination, LoadingPageComponent} from '@dcp/configurator';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {AuthStorageService, GlobalMessageService, GlobalMessageType, User} from '@spartacus/core';
import {UserAccountFacade} from '@spartacus/user/account/root';
import {Observable, of} from 'rxjs';
import {take, catchError} from 'rxjs/operators';
import {convertUrlToCurrentHostname} from './../shared/url-utils';
import {getAuthorized} from './../shared/getAuthorized';
import {formatTestPageError} from './../shared/format-error';
import {TabNavigationComponent} from '../tab-navigation/tab-navigation.component';
import {IframeComponent} from '../../shared/iframe/iframe.component';
import {CdkCopyToClipboard} from '@angular/cdk/clipboard';
import {AsyncPipe} from '@angular/common';

@UntilDestroy()
@Component({
  selector: 'dcp-contract-list-page',
  templateUrl: './product-determination-page.component.html',
  styleUrls: ['./product-determination-page.component.scss'],
  imports: [LoadingPageComponent, TabNavigationComponent, IframeComponent, CdkCopyToClipboard, AsyncPipe],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class ProductDeterminationPageComponent implements OnInit {
  technicalName = '';
  navigation =
    '[{"path": "https://www.sap.com", "mobileLinkTitle": "Secondary Navigation 1 mobile", "target": "_blank", "title": "Secondary Navigation 1"}]';
  materialNumber = '';
  sUserId = '';
  currentUser: string;
  isAuthorized: boolean;
  user$: Observable<User>;
  productDetermination$: Observable<ProductDetermination>;
  previewUrl: string;
  loadingState = false;
  loadingScreenText = 'Please wait while the cached list is updated';
  currentUserScimUserId: string;

  constructor(
    private dcpProductDeterminationService: DcpProductDeterminationService,
    private userFacade: UserAccountFacade,
    private globalMessageService: GlobalMessageService,
    private authStorageService: AuthStorageService,
    private configService: ConfigService,
    private cdr: ChangeDetectorRef,
  ) {}

  /**
   * Angular lifecycle hook. Initializes user stream and authorization check.
   */
  ngOnInit(): void {
    this.user$ = this.userFacade.get();
    this.user$.pipe(untilDestroyed(this), take(1)).subscribe((user) => {
      this.currentUserScimUserId = user.scimUserId;
    });
    this.getAuthorizedRoles();
  }

  /**
   * Handles input events from text fields and updates the corresponding properties.
   * @param event CustomEvent containing input data.
   */
  onInput(event: Event): void {
    const target = event.target as HTMLInputElement;
    switch (target.id) {
      case 'technicalName':
        this.technicalName = target.value;
        break;
      case 'materialNumber':
        this.materialNumber = target.value;
        break;
      case 'sUserId':
        this.sUserId = target.value;
        break;
      default:
        break;
    }
  }

  /**
   * Resets the product query and clears the input fields.
   */
  onReset(): void {
    this.technicalName = '';
    this.materialNumber = '';
    this.sUserId = '';
    this.productDetermination$ = of(undefined);
  }

  /**
   * Fetches product determination data and assigns it to the observable.
   * Handles errors by displaying a global message.
   * @param technicalName The technical name of the product.
   * @param materialNumber The material number of the product.
   * @param sUserId The S-User ID.
   */
  getProductDetermination(technicalName?: string, materialNumber?: string, sUserId?: string): void {
    const materialFilter = materialNumber ? [materialNumber] : [];
    this.productDetermination$ = this.dcpProductDeterminationService
      .getProductDetermination(technicalName, '', sUserId, materialFilter)
      .pipe(
        catchError((error) => {
          this.currentUser = undefined;
          this.globalMessageService.add(
            formatTestPageError(error),
            GlobalMessageType.MSG_TYPE_ERROR,
            60000,
          );
          return of(undefined);
        }),
      );
  }

  /**
   * Checks if the current user is authorized based on roles from the JWT token.
   */
  getAuthorizedRoles(): void {
    getAuthorized(this.authStorageService, this.configService, this);
  }

  convertUrlToCurrentHostname = convertUrlToCurrentHostname;

  /**
   * Checks if a preview URL is set.
   * @returns True if previewUrl is set and not empty.
   */
  hasPreviewUrl(): boolean {
    return this.previewUrl?.length > 0;
  }

  /**
   * Toggles the preview URL for the product.
   * @param targetUrl The URL to preview.
   */
  togglePreview(targetUrl: string): void {
    this.previewUrl = this.previewUrl ? null : targetUrl;
    this.cdr.detectChanges();
  }
}
